/**
 * Cross-platform OAuth browser wrapper.
 *
 * iOS:     ASWebAuthenticationSession (via openAuthSessionAsync) intercepts the custom-scheme
 *          redirect automatically and resolves the promise with the callback URL.
 *
 * Android: Chrome Custom Tabs do NOT auto-intercept custom-scheme redirects. When the OAuth
 *          flow completes, Android fires a Linking deep-link event instead. We listen for that
 *          event in parallel, dismiss the browser when it arrives, and resolve from there.
 *
 * Both platforms also dispatch SET_OAUTH_IN_PROGRESS so the app-lock logic in RootNavigator
 * doesn't trigger while the browser is open.
 */

import { Linking, Platform } from 'react-native';
import * as WebBrowser from 'expo-web-browser';
import { store } from '../store';
import { SET_OAUTH_IN_PROGRESS } from '../store/constants';
import config from '../config';

// Origin of the records API, whose health-records/callback bridge page posts
// a direct EHR sign-in's code + state. Those messages are accepted from that
// origin only: a code for someone's records must never be taken from a page
// we don't serve.
function recordsApiOrigin(): string | null {
  try {
    return new URL((config as any).recordsApi.endpoint).origin;
  } catch (_) {
    return null;
  }
}

export interface OAuthResult {
  type: 'success' | 'cancel' | 'error';
  url?: string;
}

export interface OAuthSessionOptions {
  preferEphemeralSession?: boolean;
}

export async function openOAuthSession(
  url: string,
  callbackPrefix: string,
  options?: OAuthSessionOptions,
): Promise<OAuthResult> {
  store.dispatch({ type: SET_OAUTH_IN_PROGRESS, payload: true });

  // Web flow uses a popup + postMessage handshake. expo-web-browser's web
  // shim polls `popup.closed` to know when the user finished, but Chrome's
  // Cross-Origin-Opener-Policy on accounts.google.com blocks the opener from
  // reading the popup's `.closed` property — so the promise never resolves
  // and the user gets stuck after pressing Allow. Our HTTPS callback page
  // (backend/api/client/controllers/calendar/oauth-callback) postMessages
  // the claim/error back to window.opener, so we listen for that here and
  // reconstruct the URL the native flow would have produced.
  if (Platform.OS === 'web') {
    try {
      return await new Promise<OAuthResult>((resolve) => {
        let settled = false;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        let closedPoll: ReturnType<typeof setInterval> | null = null;
        let closedGrace: ReturnType<typeof setTimeout> | null = null;
        const win = typeof window !== 'undefined' && typeof window.open === 'function'
          ? window.open(url, 'eleplan-oauth', 'width=520,height=720,scrollbars=yes,resizable=yes')
          : null;

        const settle = (result: OAuthResult) => {
          if (settled) return;
          settled = true;
          if (typeof window !== 'undefined') window.removeEventListener('message', onMessage);
          if (timeoutId != null) clearTimeout(timeoutId);
          if (closedPoll != null) clearInterval(closedPoll);
          if (closedGrace != null) clearTimeout(closedGrace);
          try { win?.close(); } catch (_) { /* cross-origin close errors are noisy and harmless */ }
          resolve(result);
        };

        const buildResultUrl = (params: Record<string, string>) => {
          const qs = Object.entries(params)
            .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
            .join('&');
          return `${callbackPrefix}${callbackPrefix.includes('?') ? '&' : '?'}${qs}`;
        };

        const onMessage = (event: MessageEvent) => {
          const data: any = event?.data;
          if (!data || data.source !== 'eleplan-oauth-callback') return;
          // Direct EHR sign-in (records health-records/callback).
          if (data.status === 'authorized' || data.status === 'error') {
            if (event.origin !== recordsApiOrigin()) return;
            if (data.status === 'authorized' && data.code && data.state) {
              const params: Record<string, string> = { status: 'authorized', code: String(data.code), state: String(data.state) };
              if (data.iss) params.iss = String(data.iss);
              settle({ type: 'success', url: buildResultUrl(params) });
            } else {
              settle({ type: 'error', url: buildResultUrl({ status: 'error', reason: String(data.reason || 'unknown') }) });
            }
            return;
          }
          // `claim` — calendar / gmail bridge pages send a server-
          // generated nonce that keys the oauth_pending_claims row.
          if (data.claim) {
            settle({ type: 'success', url: buildResultUrl({ claim: String(data.claim) }) });
            return;
          }
          // `code` — Cognito Hosted UI bridge page (auth/cognito-
          // callback) sends the OAuth authorization code directly.
          // Callers exchange it at the Cognito token endpoint
          // themselves; no server-side state needed.
          if (data.code) {
            settle({ type: 'success', url: buildResultUrl({ code: String(data.code) }) });
            return;
          }
          if (data.error) {
            settle({ type: 'error', url: buildResultUrl({ error: String(data.error) }) });
            return;
          }
        };

        if (typeof window !== 'undefined') window.addEventListener('message', onMessage);

        // The popup is gone but nothing came back. Either the user closed it
        // by hand, or the bridge page could not reach us (a COOP header on
        // the callback response severs `window.opener`, which is exactly how
        // ED-2332's sequel bit us — the button spun for the full five minutes
        // below). Settle as a cancel so the caller's spinner always ends.
        //
        // The grace period covers the ordinary race: the bridge page
        // postMessages and *then* self-closes 600ms later, so a `closed` read
        // can legitimately land while the message is still in flight. settle()
        // is idempotent, so whichever arrives first wins — and it should be
        // the message.
        //
        // `win` is null when the browser blocked the popup outright; that is a
        // cancel too, and reporting it in ~400ms beats a five-minute stall.
        closedPoll = setInterval(() => {
          let isClosed: boolean;
          try {
            isClosed = !win || win.closed;
          } catch (_) {
            // Cross-origin `closed` reads can throw under stricter policies.
            // Nothing to conclude from that — leave the timeout in charge.
            return;
          }
          if (!isClosed || closedGrace != null) return;
          closedGrace = setTimeout(() => {
            // Re-read rather than trusting the reading from 1.5s ago: a
            // cross-origin hop can blip `closed` true mid-navigation, and a
            // spurious cancel would drop a sign-in the user did finish.
            let stillClosed: boolean;
            try {
              stillClosed = !win || win.closed;
            } catch (_) {
              stillClosed = false;
            }
            if (stillClosed) {
              settle({ type: 'cancel' });
              return;
            }
            closedGrace = null;
          }, 1500);
        }, 400);

        // 5-minute hard timeout — long enough for a slow human reading the
        // consent screen, short enough that the listener doesn't leak forever
        // if the user leaves the popup open and walks away.
        timeoutId = setTimeout(() => settle({ type: 'cancel' }), 5 * 60 * 1000);
      });
    } finally {
      store.dispatch({ type: SET_OAUTH_IN_PROGRESS, payload: false });
    }
  }

  try {
    return await new Promise<OAuthResult>((resolve) => {
      let settled = false;
      let androidFallbackTimer: ReturnType<typeof setTimeout> | null = null;
      let androidInitialUrlPoller: ReturnType<typeof setInterval> | null = null;

      const normalizedPrefix = String(callbackPrefix || '').trim();
      const normalizedPrefixNoSlash = normalizedPrefix.endsWith('/')
        ? normalizedPrefix.slice(0, -1)
        : normalizedPrefix;
      const callbackSchemeBase = normalizedPrefixNoSlash.includes('://')
        ? `${normalizedPrefixNoSlash.split('://')[0]}://`
        : normalizedPrefixNoSlash;

      const isExpectedCallbackUrl = (incoming?: string | null) => {
        if (!incoming) return false;
        const urlValue = String(incoming).trim();
        const urlNoSlash = urlValue.endsWith('/') ? urlValue.slice(0, -1) : urlValue;
        // Accept exact prefix, no-trailing-slash variant, and scheme root variant.
        // Some Android providers redirect to the same scheme with a slightly different path.
        return (
          urlValue.startsWith(normalizedPrefix)
          || urlNoSlash.startsWith(normalizedPrefixNoSlash)
          || (callbackSchemeBase && urlValue.startsWith(callbackSchemeBase))
        );
      };

      const clearAndroidTimer = () => {
        if (!androidFallbackTimer) return;
        clearTimeout(androidFallbackTimer);
        androidFallbackTimer = null;
      };

      const clearAndroidPoller = () => {
        if (!androidInitialUrlPoller) return;
        clearInterval(androidInitialUrlPoller);
        androidInitialUrlPoller = null;
      };

      const settle = (result: OAuthResult) => {
        if (settled) return;
        settled = true;
        clearAndroidTimer();
        clearAndroidPoller();
        linkingSub.remove();
        resolve(result);
      };

      // Android fallback: catch the deep-link event that Chrome Custom Tabs fire
      // instead of returning a URL through openAuthSessionAsync.
      const linkingSub = Linking.addEventListener('url', ({ url: inboundUrl }) => {
        if (isExpectedCallbackUrl(inboundUrl)) {
          try { WebBrowser.dismissBrowser(); } catch (_) {}
          settle({ type: 'success', url: inboundUrl });
        }
      });

      if (Platform.OS === 'android') {
        // Some Android devices resume the app with an intent URL that can be read via
        // getInitialURL() but do not always emit a Linking "url" event reliably.
        androidInitialUrlPoller = setInterval(() => {
          Linking.getInitialURL()
            .then((initialUrl) => {
              if (isExpectedCallbackUrl(initialUrl)) {
                try { WebBrowser.dismissBrowser(); } catch (_) {}
                settle({ type: 'success', url: String(initialUrl) });
              }
            })
            .catch(() => {});
        }, 400);
      }

      // iOS primary path (also works on Android when the scheme intercept works).
      // preferEphemeralSession: true prevents the iOS ASWebAuthenticationSession reuse
      // bug that causes "network connection was lost" on consecutive OAuth calls.
      WebBrowser.openAuthSessionAsync(url, callbackPrefix, {
        preferEphemeralSession: options?.preferEphemeralSession ?? false,
      })
        .then((result) => {
          // iOS usually returns the callback URL directly.
          if ((result as OAuthResult)?.type === 'success' && (result as OAuthResult)?.url) {
            settle(result as OAuthResult);
            return;
          }

          // Android Custom Tabs often resolve as dismiss/cancel BEFORE the deep-link
          // callback event is delivered. Keep the listener alive briefly so a valid
          // callback can still be captured.
          if (Platform.OS === 'android') {
            androidFallbackTimer = setTimeout(() => {
              settle(result as OAuthResult);
            }, 10000);
            return;
          }

          settle(result as OAuthResult);
        })
        .catch(() => settle({ type: 'error' }));
    });
  } finally {
    store.dispatch({ type: SET_OAUTH_IN_PROGRESS, payload: false });
  }
}
